import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, ShieldCheck } from "lucide-react";
import { SERVICE_OPTIONS } from "@/data/content";
import { SectionHeading, Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", company: "", phone: "", email: "", service: "", message: "" };

const inputCls =
  "w-full bg-[#1B1E4E] border border-[#3FE0F2]/20 rounded-xl px-4 py-3 text-sm text-[#EEFBFF] placeholder:text-[#7D86C2] focus:outline-none focus:border-[#3FE0F2] focus:ring-1 focus:ring-[#3FE0F2]/40 transition-[border-color,box-shadow] duration-300";

export const ContactSection = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const preselected = sessionStorage.getItem("ciber_service");
    if (preselected) {
      setForm((f) => ({ ...f, service: preselected }));
      sessionStorage.removeItem("ciber_service");
    }
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        company: form.company || null,
        phone: form.phone || null,
        service: form.service || null,
        message: form.message,
      });
      toast.success("Solicitud enviada", {
        description: "Hemos recibido tu mensaje. Te contactaremos lo antes posible.",
      });
      setForm(EMPTY);
    } catch (err) {
      toast.error("No se pudo enviar", {
        description: "Inténtalo de nuevo en unos minutos o escríbenos por email.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contacto" data-testid="contact-section" className="relative bg-[#1B1E4E] py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 50% 100%, rgba(63,224,242,0.1) 0%, rgba(27,30,78,0) 60%)" }} />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14">
        <div>
          <SectionHeading
            testId="contact-heading"
            kicker="Contacto directo"
            title="Solicita información o una evaluación inicial"
            description="Cuéntanos qué quieres conseguir con la IA y te proponemos los primeros pasos, sin compromiso. Si has sufrido un ataque, indícalo en el mensaje y priorizaremos tu solicitud."
          />
          <Reveal delay={0.1}>
            <div className="flex items-start gap-4 bg-[#242764] border border-[#3FE0F2]/15 rounded-2xl p-6" data-testid="contact-assurance-card">
              <ShieldCheck size={24} className="text-[#06D6A0] shrink-0 mt-0.5" />
              <div>
                <h2 className="font-heading text-base font-semibold text-[#EEFBFF]">Confidencialidad garantizada</h2>
                <p className="mt-1 text-sm text-[#B9C3EC] leading-relaxed">
                  Toda la información que compartas se trata con confidencialidad y conforme al RGPD. Nunca te pediremos contraseñas ni datos bancarios por email.
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form onSubmit={submit} aria-busy={sending} className="bg-[#242764] border border-[#3FE0F2]/20 rounded-2xl p-7 sm:p-9 flex flex-col gap-4" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="sr-only" htmlFor="contact-name">Nombre</label>
              <input id="contact-name" name="name" autoComplete="name" required minLength={2} data-testid="contact-name-input" placeholder="Nombre *" value={form.name} onChange={set("name")} className={inputCls} />
              <label className="sr-only" htmlFor="contact-company">Empresa</label>
              <input id="contact-company" name="company" autoComplete="organization" data-testid="contact-company-input" placeholder="Empresa" value={form.company} onChange={set("company")} className={inputCls} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="sr-only" htmlFor="contact-email">Correo electrónico</label>
              <input id="contact-email" name="email" autoComplete="email" required type="email" spellCheck={false} data-testid="contact-email-input" placeholder="Correo electrónico *" value={form.email} onChange={set("email")} className={inputCls} />
              <label className="sr-only" htmlFor="contact-phone">Teléfono</label>
              <input id="contact-phone" name="phone" autoComplete="tel" type="tel" inputMode="tel" data-testid="contact-phone-input" placeholder="Teléfono" value={form.phone} onChange={set("phone")} className={inputCls} />
            </div>
            <label className="sr-only" htmlFor="contact-service">Tipo de servicio</label>
            <select
              id="contact-service"
              name="service"
              data-testid="contact-service-select"
              value={form.service}
              onChange={set("service")}
              className={`${inputCls} appearance-none ${form.service ? "" : "text-[#7D86C2]"}`}
            >
              <option value="" disabled>Tipo de servicio</option>
              {SERVICE_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
            <label className="sr-only" htmlFor="contact-message">Mensaje</label>
            <textarea
              id="contact-message"
              name="message"
              required
              minLength={10}
              rows={5}
              data-testid="contact-message-input"
              placeholder="Cuéntanos qué necesitas *"
              value={form.message}
              onChange={set("message")}
              className={`${inputCls} resize-none`}
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="mt-1 inline-flex items-center justify-center gap-2 text-sm font-semibold text-[#14163C] bg-[#3FE0F2] rounded-full px-7 py-3.5 hover:bg-[#7FEBF8] hover:shadow-[0_0_32px_rgba(63,224,242,0.4)] disabled:opacity-60 disabled:cursor-not-allowed transition-[background-color,box-shadow,opacity] duration-300"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {sending ? "Enviando…" : "Enviar solicitud"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
